/**
 * 주간 순공 랭킹 탭 (2026-07-27, 더몰입 대표 요청 / 2026-08-06 TOP20 확장)
 *
 * 서버가 그 주 순공부시간 상위 20명을 자동 기록한다. 지점은 이 기록을 보고
 * 장학생(상점·할인 부여 등)을 선정한다 — 랭킹은 계속 변하므로 마감 시점 값을 동결 저장한다.
 *
 * ⭐ 2026-08-20 — 마감 요일을 매장이 고른다(토요일 / 일요일). 여기서 바로 바꾼다.
 *    운영 규칙 페이지가 아니라 이 탭에 둔 이유: 문의한 원장이 보는 화면이 여기다.
 * ⚠️ 바꿔도 **다음 주차부터** 적용된다. 지난 주차는 그때 화면에 보였던 값이라 다시 계산하지 않는다.
 * ⚠️ 2026-08-06 이전 주차는 3명까지만 저장돼 있다(소급 생성 안 함).
 * ⚠️ API 응답 키 top3 는 레거시 이름 — 실제로는 20명까지 들어온다.
 */
import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../config/api';

interface Top3Entry {
  rank: number;
  student_name: string;
  total_minutes: number;
}

interface WeekSnapshot {
  week_start: string;
  snapshot_at: string | null;
  top3: Top3Entry[];
}

const getApiUrl = (path: string) => {
  const baseUrl = API_BASE_URL === '' ? '/api' : API_BASE_URL;
  return `${baseUrl}${path}`;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const MEDALS = ['🥇', '🥈', '🥉'];

// 매장이 3위/5위/10위 구간별로 차등 혜택(상점·할인)을 주는 운영을 해서 구간을 나눠 보여준다.
const TIERS = [
  { label: 'TOP 3', from: 1, to: 3, head: 'bg-yellow-100 text-yellow-900', row: 'bg-yellow-50 border-yellow-200' },
  { label: 'TOP 5', from: 4, to: 5, head: 'bg-slate-100 text-slate-800', row: 'bg-slate-50 border-slate-200' },
  { label: 'TOP 10', from: 6, to: 10, head: 'bg-orange-100 text-orange-900', row: 'bg-orange-50 border-orange-200' },
  { label: 'TOP 20', from: 11, to: 20, head: 'bg-gray-100 text-gray-700', row: 'bg-white border-gray-200' },
];

const formatMinutes = (mins: number) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}시간 ${m}분` : `${m}분`;
};

const formatWeekLabel = (weekStart: string) => {
  const start = new Date(weekStart + 'T00:00:00');
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const fmt = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
  return `${start.getFullYear()}년 ${fmt(start)} ~ ${fmt(end)} 주`;
};

type WeekEndDay = 'sat' | 'sun';

export const WeeklyTop3Tab: React.FC = () => {
  const [weeks, setWeeks] = useState<WeekSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weekEndDay, setWeekEndDay] = useState<WeekEndDay>('sat');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(getApiUrl('/v1/owner/study/weekly-top3?weeks=12'), {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error(`조회 실패 (${res.status})`);
        const data = await res.json();
        setWeeks(data.weeks || []);
        if (data.week_end_day === 'sun' || data.week_end_day === 'sat') {
          setWeekEndDay(data.week_end_day);
        }
      } catch (e: any) {
        setError(e.message || '주간 TOP3 조회 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const changeWeekEndDay = async (day: WeekEndDay) => {
    if (day === weekEndDay || saving) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      // 운영 규칙은 /api/v1/rules 하나만 열려 있다 (/v1/rules 프록시 없음)
      const res = await fetch(`${API_BASE_URL}/api/v1/rules/weekly-ranking`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ week_end_day: day }),
      });
      if (!res.ok) throw new Error(`저장 실패 (${res.status})`);
      setWeekEndDay(day);
      setSaveMsg(
        day === 'sun'
          ? '월~일로 바뀌었습니다. 이번 주부터 일요일 공부시간이 함께 집계됩니다.'
          : '월~토로 바뀌었습니다. 이번 주부터 토요일 오후 6시에 기록됩니다.'
      );
    } catch (e: any) {
      setSaveMsg(e.message || '설정을 저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-500">로딩 중...</span>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 py-8 text-center">{error}</div>;
  }

  return (
    <div>
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
        {weekEndDay === 'sun' ? (
          <>
            매주 <b>일요일까지</b>의 순공부시간(월~일)을 합산해, 일요일이 끝난 뒤
            <b> 월요일 새벽</b>에 <b>상위 20명</b>이 자동 기록됩니다.
          </>
        ) : (
          <>
            매주 <b>토요일 오후 6시</b>에 그 주(월~토) 순공부시간 <b>상위 20명</b>이 자동 기록됩니다.
          </>
        )}{' '}
        <b>TOP 3 / 5 / 10 / 20</b> 구간으로 나눠 보여드리니 장학생 선정(상점·할인 부여) 시 참고하세요.
        퇴원한 학생도 기록 당시 이름으로 보존됩니다.
        <div className="mt-1 text-xs text-blue-700">
          ※ 20위까지 기록은 이번 기능 적용 이후 주차부터입니다. 그 전 주차는 3명까지만 남아 있습니다
          (지난 주차는 그때 화면에 보였던 값이라 소급 생성하지 않습니다).
        </div>
      </div>

      {/* 마감 요일 선택 — 일요일 공부량이 주간 순위에서 빠지던 문제(2026-08-20) */}
      <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-gray-900">주간 집계에 포함할 요일</span>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            {([
              { key: 'sat' as WeekEndDay, label: '월~토 (토요일까지)' },
              { key: 'sun' as WeekEndDay, label: '월~일 (일요일까지)' },
            ]).map((opt) => (
              <button
                key={opt.key}
                type="button"
                disabled={saving}
                onClick={() => changeWeekEndDay(opt.key)}
                className={`px-3 py-1.5 text-sm ${
                  weekEndDay === opt.key
                    ? 'bg-blue-600 text-white font-semibold'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {saving && <span className="text-xs text-gray-500">저장 중...</span>}
        </div>
        <div className="mt-2 text-xs text-gray-600">
          일요일에 공부하는 학생이 많다면 <b>월~일</b>을 고르세요. 바꾸면 <b>이번 주차부터</b> 적용되며,
          이미 기록된 지난 주차는 그대로 둡니다(그때 보셨던 값이라 다시 계산하지 않습니다).
          <br />
          ※ 전광판(TV)의 주간 순위는 이 설정과 상관없이 원래부터 일요일까지 포함해 실시간으로 보여줍니다.
        </div>
        {saveMsg && <div className="mt-2 text-sm text-blue-700">{saveMsg}</div>}
      </div>

      {weeks.length === 0 ? (
        <div className="text-gray-500 py-12 text-center">
          아직 기록이 없습니다. 첫 기록은{' '}
          {weekEndDay === 'sun' ? '이번 주 일요일이 끝난 뒤 월요일 새벽에' : '다가오는 토요일 오후 6시에'}{' '}
          생성됩니다.
        </div>
      ) : (
        <div className="space-y-4">
          {weeks.map((w) => (
            <div key={w.week_start} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">{formatWeekLabel(w.week_start)}</h3>
                {w.snapshot_at && (
                  <span className="text-xs text-gray-400">
                    기록: {new Date(w.snapshot_at).toLocaleString('ko-KR')}
                  </span>
                )}
              </div>
              <div className="space-y-3">
                {TIERS.map((tier) => {
                  const members = (w.top3 || []).filter((t) => t.rank >= tier.from && t.rank <= tier.to);
                  if (members.length === 0) return null;
                  return (
                    <div key={tier.label}>
                      <div className={`inline-block rounded px-2 py-0.5 text-xs font-semibold mb-2 ${tier.head}`}>
                        {tier.label}
                        <span className="ml-1 font-normal">
                          ({tier.from === tier.to ? `${tier.from}위` : `${tier.from}~${tier.to}위`})
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {members.map((t) => (
                          <div
                            key={t.rank}
                            className={`flex items-center gap-3 rounded-lg px-3 py-2 border ${tier.row}`}
                          >
                            <span className="text-lg w-9 shrink-0 text-center font-semibold text-gray-700">
                              {MEDALS[t.rank - 1] || `${t.rank}위`}
                            </span>
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 truncate">{t.student_name}</div>
                              <div className="text-sm text-gray-600">{formatMinutes(t.total_minutes)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
